import React from "react";
import { withTranslation } from "react-i18next";
import { Button, Row, Col, Form, Input, FormGroup, FormFeedback, Spinner } from "reactstrap";
import { useFormik } from "formik";
import { useChangeMutation } from "services/auth/api";
import { useToasts } from "utils/useHotToast";
import ToastNotification from "components/Toast/ToastNotification";
import check from "assets/icons/check-circle.svg";
import slash from "assets/icons/slash-circle.svg";
import { getUserAccountSchema } from "model/_base/admin/password.schema";

const UserForm = ({ data, onSwitchView, isEditMode = true, isAdmin = false, formRef }) => {
  const { addToast, removeToast } = useToasts();

  // API
  const [changePassword] = useChangeMutation();
  const [showOldPassword, setShowOldPassword] = React.useState(false);
  const [showPassword, setShowPassword] = React.useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = React.useState(false);

  // Expose formik methods to parent via ref if provided
  React.useImperativeHandle(formRef, () => ({
    submitForm: validation.submitForm,
    values: validation.values,
    isValid: validation.isValid,
    errors: validation.errors,
    touched: validation.touched,
  }));

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      email: data?.email,
      oldPassword: "",
      password: "",
      confirmPassword: "",
    },
    validationSchema: getUserAccountSchema(isEditMode),
    onSubmit: async (values, { setSubmitting }) => {
      if (isEditMode) {
        try {
          const { oldPassword, password } = values;
          await changePassword({ oldPassword, password });
          const toastId = addToast(<ToastNotification icon={check} title="บันทึกข้อมูลสำเร็จ" description="รหัสผ่านของคุณได้รับการอัพเดตเรียบร้อยแล้ว" onPrimaryClick={() => removeToast(toastId)} onSecondaryClick={() => removeToast(toastId)} variant="success" />, { autoDismiss: true, appearance: "success" });
          if (onSwitchView) {
            onSwitchView();
          }
        } catch (error) {
          const toastId = addToast(<ToastNotification icon={slash} title="เกิดข้อผิดพลาด" description={"ไม่สามารถบันทึกข้อมูลได้ กรุณาลองใหม่อีกครั้ง"} onSecondaryClick={() => removeToast(toastId)} variant="danger" />, { autoDismiss: false, appearance: "error" });
        } finally {
          setSubmitting(false);
        }
      }
    },
  });

  const handleCancel = () => {
    if (isEditMode && onSwitchView) {
      onSwitchView();
    }
  };

  return (
    <Form onSubmit={validation.handleSubmit}>
      <div className="d-flex flex-column mt-3 gap-4">
        <Row>
          <Col xs={12}>
            <div className="text-2xl whitespace-nowrap">อีเมล</div>
            <Input name="email" type="email" placeholder="example@example.com" disabled={isEditMode || validation.isSubmitting} value={validation.values.email || ""} onChange={validation.handleChange} onBlur={validation.handleBlur} invalid={validation.touched.email && validation.errors.email} />
            {validation.touched.email && validation.errors.email && <FormFeedback type="invalid">{validation.errors.email}</FormFeedback>}
          </Col>
        </Row>
        <FormGroup className="space-y-4">
          <div className="form-horizontal w-full d-flex flex-column gap-4">
            {isEditMode && (
              <div>
                <div className="text-2xl whitespace-nowrap">รหัสผ่านปัจจุบัน</div>
                <div className="password-input-wrapper">
                  <Input
                    name="oldPassword"
                    type={showOldPassword ? "text" : "password"}
                    className="form-control form-control-lg"
                    placeholder="รหัสผ่านปัจจุบัน"
                    onChange={validation.handleChange}
                    onBlur={validation.handleBlur}
                    value={validation.values.oldPassword}
                    disabled={validation.isSubmitting}
                    invalid={validation.touched.oldPassword && !!validation.errors.oldPassword}
                  />
                  <button
                    className="password-toggle-btn"
                    type="button"
                    onClick={() => setShowOldPassword(!showOldPassword)}
                    disabled={validation.isSubmitting}
                  >
                    <i className={showOldPassword ? "mdi mdi-eye-off-outline" : "mdi mdi-eye-outline"}></i>
                  </button>
                </div>
                {validation.touched.oldPassword && validation.errors.oldPassword && <FormFeedback type="invalid" className="d-block">{validation.errors.oldPassword}</FormFeedback>}
              </div>
            )}
            <div>
              <div className="text-2xl whitespace-nowrap">รหัสผ่าน {isAdmin ? "" : "ใหม่"}</div>
              <div className="password-input-wrapper">
                <Input
                  name="password"
                  type={showPassword ? "text" : "password"}
                  className="form-control form-control-lg"
                  placeholder="รหัสผ่าน"
                  onChange={validation.handleChange}
                  onBlur={validation.handleBlur}
                  value={validation.values.password}
                  disabled={validation.isSubmitting}
                  invalid={validation.touched.password && validation.errors.password ? true : false}
                />
                <button
                  className="password-toggle-btn"
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  disabled={validation.isSubmitting}
                >
                  <i className={showPassword ? "mdi mdi-eye-off-outline" : "mdi mdi-eye-outline"}></i>
                </button>
              </div>
              <p className="text-gray-500 pt-2 text-sm m-0">รหัสผ่านต้องมีอย่างน้อย 8 ตัวอักษร ประกอบด้วยตัวพิมพ์ใหญ่ ตัวพิมพ์เล็ก ตัวเลข และอักขระพิเศษ</p>
              {validation.touched.password && validation.errors.password && <FormFeedback type="invalid" className="d-block">{validation.errors.password}</FormFeedback>}
            </div>

            <div>
              <div className="text-2xl whitespace-nowrap">ยืนยันรหัสผ่าน {isAdmin ? "" : "ใหม่"}</div>
              <div className="password-input-wrapper">
                <Input
                  name="confirmPassword"
                  type={showConfirmPassword ? "text" : "password"}
                  className="form-control form-control-lg"
                  placeholder="ยืนยันรหัสผ่าน"
                  value={validation.values.confirmPassword}
                  onChange={validation.handleChange}
                  onBlur={validation.handleBlur}
                  disabled={validation.isSubmitting}
                  invalid={validation.touched.confirmPassword && validation.errors.confirmPassword ? true : false}
                />
                <button
                  className="password-toggle-btn"
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  disabled={validation.isSubmitting}
                >
                  <i className={showConfirmPassword ? "mdi mdi-eye-off-outline" : "mdi mdi-eye-outline"}></i>
                </button>
              </div>
              {validation.touched.confirmPassword && validation.errors.confirmPassword && <FormFeedback type="invalid" className="d-block">{validation.errors.confirmPassword}</FormFeedback>}
            </div>
          </div>
        </FormGroup>
      </div>
      {isEditMode && (
        <div className="text-end mt-4">
          <div className="d-flex justify-content-between gap-3 w-100">
            <Button color="light" outline className="w-52" onClick={handleCancel}>
              ยกเลิก
            </Button>
            <Button className="w-52" color="primary" type="submit" disabled={validation.isSubmitting}>
              บันทึก
            </Button>
          </div>
        </div>
      )}
    </Form>
  );
};

export default withTranslation()(UserForm);
